import { useEffect, useRef, useState } from 'react';

interface FlipCardProps {
  icon: string;
  word: string;
  desc: string;
  color: string;
  delay: number;
  visible: boolean;
}

function FlipCard({ icon, word, desc, color, delay, visible }: FlipCardProps) {
  const [flipped, setFlipped] = useState(false);

  return (
    <div
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
      style={{ transitionDelay: `${delay}ms`, perspective: '1000px', flex: '1 1 0' }}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      <div
        className="relative w-full h-full cursor-pointer"
        style={{
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: '420px',
        }}
      >
        {/* Front */}
        <div
          className="absolute inset-0 rounded-3xl flex flex-col items-center justify-center gap-6 p-8"
          style={{ backfaceVisibility: 'hidden', background: '#fff' }}
        >
          <div className="w-32 h-32 flex items-center justify-center">
            <i className={`${icon} text-8xl`} style={{ color, filter: `drop-shadow(0 0 20px ${color}80)` }} />
          </div>
          <p className="boldonse text-4xl" style={{ color }}>{word}</p>
        </div>

        {/* Back */}
        <div
          className="absolute inset-0 rounded-3xl flex flex-col items-center justify-center gap-4 p-8"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', background: color }}
        >
          <p className="text-white text-center text-lg font-medium leading-relaxed">{desc}</p>
          <div className="w-12 h-1 bg-white/40 rounded-full mt-2" />
        </div>
      </div>
    </div>
  );
}

const cards = [
  {
    icon: 'ri-lightbulb-flash-line',
    word: 'INNOVATION',
    desc: 'Cutting-edge technologies and creative solutions for your business growth',
    color: '#8EC9BB',
  },
  {
    icon: 'ri-speed-line',
    word: 'AGILE',
    desc: 'Fast iterations, transparent process, and adaptive development workflows',
    color: '#FA6E4F',
  },
  {
    icon: 'ri-expand-diagonal-line',
    word: 'SCALABLE',
    desc: 'Architecture designed to grow with your business from startup to enterprise',
    color: '#FB8E7E',
  },
  {
    icon: 'ri-shield-keyhole-line',
    word: 'SECURE',
    desc: 'Enterprise-grade security practices and data protection at every layer',
    color: '#111',
  },
];

export default function FlipCardsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="w-full min-h-screen flex flex-col" style={{ background: '#F2CF59' }}>
      {/* Header */}
      <div className="px-10 pt-20 pb-12">
        <div
          className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}
        >
          <p className="text-[#FA6E4F]/60 text-xs tracking-[0.5em] uppercase mb-6">Why choose us</p>
          <h2 className="boldonse text-[#FA6E4F] leading-none" style={{ fontSize: 'clamp(60px, 10vw, 140px)' }}>
            WHY US
          </h2>
        </div>
      </div>

      {/* Cards */}
      <div className="flex-1 flex gap-8 px-10 pb-20" style={{ minHeight: '420px' }}>
        {cards.map((card, i) => (
          <FlipCard
            key={card.word}
            {...card}
            delay={i * 120}
            visible={visible}
          />
        ))}
      </div>
    </section>
  );
}